import * as React from 'react'
import { Link } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CalendarClock,
  ClipboardCheck,
  Copy,
  GaugeCircle,
  History,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  Presentation,
  Target,
  Trash2,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { displayName, formatDate, formatMetric, nextPeriodLabel, trendOf } from '@/lib/format'
import type {
  Activity,
  ActivityCompletion,
  Evaluation,
  Metric,
  MetricValue,
  Profile,
  TeamGoal,
  Workshop,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { CopyChartButton, useCopyChart } from '@/components/copy-chart-button'
import { goalIcon } from '@/lib/goal-icons'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { MetricEditor } from '@/pages/metric-editor'
import { GoalEditor } from '@/pages/goal-editor'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

interface DashboardData {
  goals: TeamGoal[]
  metrics: Metric[]
  values: MetricValue[]
  activities: Activity[]
  completions: ActivityCompletion[]
  workshops: Workshop[]
  evaluations: Evaluation[]
  profiles: Profile[]
}

export function DashboardPage() {
  const { currentTeam, isLead, session } = useAuth()
  const teamId = currentTeam?.id
  // What the readings dialog is pointed at. `edit` decides whether it opens on
  // the history list or straight into a form.
  const [readings, setReadings] = React.useState<{
    metric: Metric
    edit?: MetricValue | 'new'
  } | null>(null)
  const [defining, setDefining] = React.useState(false)
  const [editingMetric, setEditingMetric] = React.useState<Metric | null>(null)
  // `'new'` opens the editor empty; a goal opens it on that row.
  const [editingGoal, setEditingGoal] = React.useState<TeamGoal | 'new' | null>(null)

  const { data, loading, error, refetch } = useQuery<DashboardData>(async () => {
    if (!teamId) {
      return { goals: [], metrics: [], values: [], activities: [], completions: [], workshops: [], evaluations: [], profiles: [] }
    }

    const [goals, metrics, values, activities, completions, workshops, evaluations, profiles] = await Promise.all([
      // The lead's order, with created_at breaking ties among goals added before
      // anything was reordered.
      sb().from('team_goals').select('*').eq('team_id', teamId)
        .order('display_order').order('created_at'),
      sb().from('metrics').select('*').eq('team_id', teamId).eq('is_active', true)
        .order('display_order').order('label'),
      sb().from('metric_values').select('*').eq('team_id', teamId).order('period_start'),
      sb().from('activities').select('*').eq('team_id', teamId),
      sb().from('activity_completions').select('*').eq('team_id', teamId),
      sb().from('workshops').select('*').eq('team_id', teamId).order('starts_at'),
      sb().from('evaluations').select('*').eq('team_id', teamId).order('conducted_on', { ascending: false }),
      // RLS limits this to teammates, so it can't leak beyond the team.
      sb().from('profiles').select('*'),
    ])
    for (const r of [goals, metrics, values, activities, completions, workshops, evaluations, profiles]) {
      if (r.error) throw r.error
    }
    return {
      goals: (goals.data ?? []) as TeamGoal[],
      metrics: (metrics.data ?? []) as Metric[],
      values: (values.data ?? []) as MetricValue[],
      activities: (activities.data ?? []) as Activity[],
      completions: (completions.data ?? []) as ActivityCompletion[],
      workshops: (workshops.data ?? []) as Workshop[],
      evaluations: (evaluations.data ?? []) as Evaluation[],
      profiles: (profiles.data ?? []) as Profile[],
    }
  }, [teamId])

  const valuesByMetric = React.useMemo(() => {
    const map = new Map<string, MetricValue[]>()
    for (const v of data?.values ?? []) {
      const list = map.get(v.metric_id)
      if (list) list.push(v)
      else map.set(v.metric_id, [v])
    }
    // metric_values arrives ordered by period_start, so each list is already
    // chronological — oldest first, which is what the sparkline wants.
    return map
  }, [data?.values])

  const nameFor = React.useCallback(
    (userId: string | null) => {
      if (!userId) return '—'
      if (userId === session?.user?.id) return 'You'
      const p = data?.profiles.find((x) => x.user_id === userId)
      return p ? displayName(p) : 'Someone'
    },
    [data?.profiles, session?.user?.id],
  )

  // Every hook must run before this bails out, so the early return lives below
  // them rather than at the top of the component.
  if (!currentTeam) {
    return (
      <Page title="Dashboard">
        <EmptyState icon={GaugeCircle} title="No team selected" />
      </Page>
    )
  }

  return (
    <Page
      title="Dashboard"
      description={describeTeam(currentTeam.started_on, currentTeam.sprint_length_days)}
      actions={
        isLead && (
          <Button variant="outline" onClick={() => setDefining(true)}>
            <Plus /> New metric
          </Button>
        )
      }
    >
      {error && <ErrorNote error={error} onRetry={() => void refetch()} />}
      {/* Three branches, and the middle one matters: when the first read FAILS
          there is no data, and rendering the body anyway produced a dashboard
          that said "No topics scheduled yet", "Nothing on the calendar" and
          "None run yet" underneath the error — three confident statements about
          a team whose rows never arrived. A page that could not load says so
          once and shows nothing else. A refetch that fails keeps the data it
          already had, which is still true, and puts the banner above it. */}
      {loading && !data ? (
        <Loading />
      ) : !data ? null : (
        <div className="space-y-6">
          {/* Above the metrics on purpose: the goals say what the numbers are
              for, so they're the first thing the room reads. Members with no
              goals set see nothing rather than an empty section they can't
              fill. */}
          {data && (data.goals.length > 0 || isLead) && (
            <GoalsSection
              goals={data.goals}
              isLead={isLead}
              onAdd={() => setEditingGoal('new')}
              onEdit={setEditingGoal}
            />
          )}

          {data && data.metrics.length === 0 ? (
            <EmptyState
              icon={GaugeCircle}
              title="No metrics yet"
              description={
                isLead
                  ? 'Define what this team tracks — Points/Sprint, code coverage, cycle time — then record a reading each sprint.'
                  : 'Your team lead or DPL coach hasn’t defined any metrics yet.'
              }
              action={
                isLead && (
                  <Button onClick={() => setDefining(true)}>
                    <Plus /> Define the first metric
                  </Button>
                )
              }
            />
          ) : (
            <section>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {data?.metrics.map((m) => (
                  <MetricTile
                    key={m.id}
                    metric={m}
                    values={valuesByMetric.get(m.id) ?? []}
                    isLead={isLead}
                    onRecord={() => setReadings({ metric: m, edit: 'new' })}
                    onHistory={() => setReadings({ metric: m })}
                    onEditMetric={() => setEditingMetric(m)}
                  />
                ))}
              </div>
            </section>
          )}

          {data && data.metrics.length > 0 && (
            <MetricTrend
              metrics={data.metrics}
              valuesByMetric={valuesByMetric}
              onEditReading={(metric, value) => setReadings({ metric, edit: value })}
              onHistory={(metric) => setReadings({ metric })}
            />
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            <ActivityProgress
              activities={data?.activities ?? []}
              completions={data?.completions ?? []}
              userId={session?.user?.id}
            />
            <UpcomingWorkshops workshops={data?.workshops ?? []} />
            <LatestEvaluation evaluations={data?.evaluations ?? []} />
          </div>
        </div>
      )}

      {readings && (
        <ReadingsDialog
          metric={readings.metric}
          values={valuesByMetric.get(readings.metric.id) ?? []}
          nameFor={nameFor}
          initialEdit={readings.edit}
          onClose={() => setReadings(null)}
          onChanged={() => void refetch()}
        />
      )}

      {editingGoal && teamId && (
        <GoalEditor
          teamId={teamId}
          goal={editingGoal === 'new' ? undefined : editingGoal}
          siblings={data?.goals ?? []}
          onClose={() => setEditingGoal(null)}
          onSaved={() => {
            setEditingGoal(null)
            void refetch()
          }}
        />
      )}

      {editingMetric && (
        <MetricEditor
          teamId={editingMetric.team_id}
          metric={editingMetric}
          onClose={() => setEditingMetric(null)}
          onSaved={() => {
            setEditingMetric(null)
            void refetch()
          }}
        />
      )}

      {defining && (
        <MetricEditor
          teamId={currentTeam.id}
          onClose={() => setDefining(false)}
          onSaved={() => {
            setDefining(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

function describeTeam(startedOn: string | null, sprintDays: number): string {
  const parts: string[] = []
  if (startedOn) parts.push(`Started ${formatDate(startedOn)}`)
  parts.push(`${sprintDays}-day sprints`)
  return parts.join(' · ')
}

/**
 * What the team is trying to achieve, in the team's own words — a card per goal,
 * title and description, nothing derived.
 *
 * Leads write them; everyone reads them. There's no progress bar and no status
 * here on purpose: whether a goal is being met is what the metrics below are
 * for, and a percentage nobody maintains reads as truth.
 */
function GoalsSection({
  goals,
  isLead,
  onAdd,
  onEdit,
}: {
  goals: TeamGoal[]
  isLead: boolean
  onAdd: () => void
  onEdit: (goal: TeamGoal) => void
}) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-medium">
          <Target className="h-4 w-4 text-muted-foreground" /> Goals
          {goals.length > 0 && (
            <span className="text-xs font-normal tabular-nums text-muted-foreground">
              {goals.length}
            </span>
          )}
        </h2>
        {isLead && goals.length > 0 && (
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus /> Add goal
          </Button>
        )}
      </div>

      {goals.length === 0 ? (
        <Card className="border-dashed shadow-none">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground">
              No goals yet. Say what this accelerator is for — it's the first thing the room reads.
            </p>
            <Button variant="link" size="sm" className="mt-1 h-auto p-0" onClick={onAdd}>
              Add the first goal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {goals.map((g) => {
            const Icon = goalIcon(g.icon)
            return (
            <Card key={g.id}>
              <CardHeader className={cn(g.description && 'pb-2')}>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="flex min-w-0 items-start gap-2 text-base leading-snug">
                    {/* Decorative: the title next to it already says what the
                        goal is, so the icon is not announced twice. */}
                    <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span className="min-w-0">{g.title}</span>
                  </CardTitle>
                  {isLead && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="-mr-1 -mt-1 shrink-0"
                      onClick={() => onEdit(g)}
                      aria-label={`Edit goal: ${g.title}`}
                    >
                      <Pencil />
                    </Button>
                  )}
                </div>
              </CardHeader>
              {g.description && (
                <CardContent>
                  {/* Typed as prose, so line breaks the lead put in are kept. */}
                  <p className="whitespace-pre-line text-sm text-muted-foreground">
                    {g.description}
                  </p>
                </CardContent>
              )}
            </Card>
            )
          })}
        </div>
      )}
    </section>
  )
}

/**
 * A stat tile, not a one-bar chart: the reader's job is "what is it now, and is
 * it moving the right way", which is a number plus a delta plus a sparkline.
 */
function MetricTile({
  metric,
  values,
  isLead,
  onRecord,
  onHistory,
  onEditMetric,
}: {
  metric: Metric
  values: MetricValue[]
  isLead: boolean
  onRecord: () => void
  onHistory: () => void
  onEditMetric: () => void
}) {
  const latest = values[values.length - 1]
  const previous = values[values.length - 2]
  const trend = latest ? trendOf(metric, latest.value, previous?.value) : null

  const spark = values.slice(-12).map((v) => ({ label: v.period_label, value: v.value }))

  // The sparkline is 48px tall and lives in a card with a number and a trend
  // chip, so a copy button beside it would be more chrome than chart. It goes
  // in the menu that is already there instead.
  const cardRef = React.useRef<HTMLDivElement>(null)
  const { copy: copySpark } = useCopyChart(cardRef, `${metric.label} trend`)

  return (
    <Card ref={cardRef}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="truncate text-sm font-medium text-muted-foreground">
              {metric.label}
            </CardTitle>
            {metric.description && (
              <CardDescription className="mt-0.5 line-clamp-1 text-xs">
                {metric.description}
              </CardDescription>
            )}
          </div>
          <div className="flex shrink-0 items-center">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRecord}
              aria-label={`Record a ${metric.label} reading`}
            >
              <Plus />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={`${metric.label} options`}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onSelect={onRecord}>
                  <Plus /> Record a reading
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onHistory} disabled={values.length === 0}>
                  <History /> Edit past readings…
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => void copySpark()}
                  disabled={spark.length < 2}
                >
                  <Copy /> Copy chart as image
                </DropdownMenuItem>
                {isLead && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onEditMetric}>
                      <Pencil /> Edit this metric
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {latest ? (
          <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-3xl font-semibold tabular-nums tracking-tight">
                {formatMetric(latest.value, metric.unit)}
              </span>
              {trend && <TrendChip trend={trend} unit={metric.unit} />}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              <button
                type="button"
                onClick={onHistory}
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                title={`Edit ${metric.label} readings`}
                aria-label={`Edit ${metric.label} readings — latest is ${latest.period_label}`}
              >
                {latest.period_label}
              </button>
              {metric.target != null && ` · target ${formatMetric(metric.target, metric.unit)}`}
              {values.length > 1 && ` · ${values.length} readings`}
            </p>

            {spark.length > 1 && (
              <div className="mt-3 h-12">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spark} margin={{ top: 2, right: 2, bottom: 0, left: 2 }}>
                    <defs>
                      <linearGradient id={`spark-${metric.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--chart-line)" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="var(--chart-line)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <Tooltip
                      content={<SparkTooltip unit={metric.unit} />}
                      cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="var(--chart-line)"
                      strokeWidth={2}
                      fill={`url(#spark-${metric.id})`}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : (
          <div className="py-2">
            <p className="text-sm text-muted-foreground">No readings yet.</p>
            <Button variant="link" size="sm" className="mt-1 h-auto p-0" onClick={onRecord}>
              Record the first one
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TrendChip({
  trend,
  unit,
}: {
  trend: NonNullable<ReturnType<typeof trendOf>>
  unit: Metric['unit']
}) {
  if (trend.delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Minus className="h-3 w-3" /> no change
      </span>
    )
  }
  const Icon = trend.delta > 0 ? ArrowUpRight : ArrowDownRight
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-medium tabular-nums',
        trend.better ? 'text-better' : 'text-worse',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {trend.delta > 0 ? '+' : ''}
      {formatMetric(trend.delta, unit)}
      {trend.pct != null && ` (${trend.pct > 0 ? '+' : ''}${trend.pct.toFixed(0)}%)`}
    </span>
  )
}

function SparkTooltip({
  active,
  payload,
  unit,
}: {
  active?: boolean
  payload?: Array<{ payload: { label: string; value: number } }>
  unit: Metric['unit']
}) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-2 py-1 text-xs shadow-md">
      <div className="font-medium">{p.label}</div>
      <div className="tabular-nums text-muted-foreground">{formatMetric(p.value, unit)}</div>
    </div>
  )
}

/** Full-size trend for one metric at a time — one series, so no legend. */
function MetricTrend({
  metrics,
  valuesByMetric,
  onEditReading,
  onHistory,
}: {
  metrics: Metric[]
  valuesByMetric: Map<string, MetricValue[]>
  onEditReading: (metric: Metric, value: MetricValue) => void
  onHistory: (metric: Metric) => void
}) {
  // The whole card, not just the plot: `findChartSvg` reaches into it for the
  // recharts <svg>, and hanging the ref here means the Select and the buttons
  // can move around in the header without the copy breaking.
  const chartRef = React.useRef<HTMLDivElement>(null)

  // A single reading has no trend to draw, but it's still data someone may need
  // to correct — so the selector includes it and the chart just shows one point.
  const withData = metrics.filter((m) => (valuesByMetric.get(m.id)?.length ?? 0) > 0)
  const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined)

  // Default to the first metric that actually has a trend to show, and recover
  // if the selected metric loses its data or is deleted.
  const selected = withData.find((m) => m.id === selectedId) ?? withData[0]

  if (!selected) return null

  const values = valuesByMetric.get(selected.id) ?? []
  // Carry the row id on each point so a click can resolve back to the reading.
  const series = values.map((v) => ({ label: v.period_label, value: v.value, id: v.id }))

  return (
    <Card ref={chartRef}>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base">{selected.label} over time</CardTitle>
          <CardDescription>
            {values.length} {values.length === 1 ? 'reading' : 'readings'} · click a point to
            correct it
            {selected.target != null && ' · dashed line is the target'}
          </CardDescription>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {withData.length > 1 && (
            <Select value={selected.id} onValueChange={setSelectedId}>
              <SelectTrigger className="w-[190px] shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {withData.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => onHistory(selected)}>
            <History /> Readings
          </Button>
          <CopyChartButton targetRef={chartRef} name={`${selected.label} over time`} label="Copy" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={series}
              margin={{ top: 8, right: 16, bottom: 4, left: -8 }}
              onClick={(e: any) => {
                const id = e?.activePayload?.[0]?.payload?.id
                const hit = values.find((v) => v.id === id)
                if (hit) onEditReading(selected, hit)
              }}
              className="cursor-pointer"
            >
              <CartesianGrid stroke="hsl(var(--chart-grid))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                stroke="hsl(var(--chart-grid))"
                tickMargin={8}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                stroke="hsl(var(--chart-grid))"
                width={52}
                tickFormatter={(v: number) => formatMetric(v, selected.unit)}
              />
              <Tooltip
                content={<SparkTooltip unit={selected.unit} />}
                cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' }}
              />
              {selected.target != null && (
                <ReferenceLine
                  y={selected.target}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="5 4"
                  strokeWidth={1.5}
                  label={{
                    value: 'target',
                    position: 'right',
                    fontSize: 10,
                    fill: 'hsl(var(--muted-foreground))',
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="value"
                stroke="var(--chart-line)"
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 0, fill: 'var(--chart-line)' }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: 'hsl(var(--background))', cursor: 'pointer' }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Readings manager: the full history for one metric, with every row editable.
 *
 * Two modes in one dialog rather than a nested one — the list, and the form for
 * whichever row you picked. Editing old data is the point: a metric's history is
 * the thing the dashboard charts, and a number typed into the wrong sprint is
 * worse than no number at all.
 */
function ReadingsDialog({
  metric,
  values,
  nameFor,
  initialEdit,
  onClose,
  onChanged,
}: {
  metric: Metric
  /** Chronological, oldest first (as the dashboard query returns them). */
  values: MetricValue[]
  nameFor: (userId: string | null) => string
  /** Open straight into the form: a MetricValue to edit, or 'new'. */
  initialEdit?: MetricValue | 'new'
  onClose: () => void
  onChanged: () => void
}) {
  const [mode, setMode] = React.useState<MetricValue | 'new' | null>(initialEdit ?? null)

  // The form underneath owns the typing; the guard lives here because this is
  // the component Escape and a click outside actually close.
  const [formDirty, setFormDirty] = React.useState(false)
  const guard = useDiscardGuard(formDirty, onClose)

  // Newest first for reading; the trend chart wants the other order.
  const history = React.useMemo(() => [...values].reverse(), [values])

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-xl">
        <DiscardPrompt guard={guard} what="this reading" />
        {mode ? (
          <ReadingForm
            metric={metric}
            values={values}
            editing={mode === 'new' ? undefined : mode}
            onDirty={setFormDirty}
            onCancel={() =>
              guard.requestCloseTo(() => {
                setFormDirty(false)
                if (initialEdit) onClose()
                else setMode(null)
              })
            }
            onSaved={() => {
              setFormDirty(false)
              onChanged()
              if (initialEdit) onClose()
              else setMode(null)
            }}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{metric.label} readings</DialogTitle>
              <DialogDescription>
                {values.length === 0
                  ? 'No readings yet.'
                  : `${values.length} recorded. Edit any one to correct it.`}
              </DialogDescription>
            </DialogHeader>

            <div className="my-2 max-h-[55vh] overflow-y-auto">
              {history.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nothing recorded yet.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-3 font-medium">Period</th>
                      <th className="pb-2 px-3 text-right font-medium">Value</th>
                      <th className="pb-2 px-3 font-medium">Recorded</th>
                      <th className="pb-2 pl-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((v) => (
                      <tr key={v.id} className="group border-b last:border-0">
                        <td className="py-2 pr-3">
                          <div className="font-medium">{v.period_label}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatDate(v.period_start)}
                          </div>
                          {v.note && (
                            <div className="mt-0.5 text-xs text-muted-foreground">{v.note}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right font-medium tabular-nums">
                          {formatMetric(v.value, metric.unit)}
                        </td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">
                          {nameFor(v.recorded_by)}
                        </td>
                        <td className="py-2 pl-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                            onClick={() => setMode(v)}
                          >
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button type="button" onClick={() => setMode('new')}>
                <Plus /> Record a reading
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * Add or correct one reading.
 *
 * A NEW reading upserts on (metric_id, period_label) so re-entering a period
 * corrects it rather than duplicating. An EDIT updates by primary key instead,
 * which is what lets the period label itself be renamed — an upsert would create
 * a second row under the new name and leave the old one behind.
 */
function ReadingForm({
  metric,
  values,
  editing,
  onDirty,
  onCancel,
  onSaved,
}: {
  metric: Metric
  values: MetricValue[]
  editing?: MetricValue
  /** Reported upward so the dialog that Escape closes knows there is something to lose. */
  onDirty: (dirty: boolean) => void
  onCancel: () => void
  onSaved: () => void
}) {
  const last = values[values.length - 1]
  const [period, setPeriod] = React.useState(
    editing?.period_label ?? nextPeriodLabel(metric, last?.period_label),
  )
  const [value, setValue] = React.useState(editing ? String(editing.value) : '')
  const [note, setNote] = React.useState(editing?.note ?? '')
  const [periodStart, setPeriodStart] = React.useState(
    editing?.period_start ?? new Date().toISOString().slice(0, 10),
  )
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ period, value, note, periodStart })
  React.useEffect(() => onDirty(dirty), [dirty, onDirty])

  // When adding, typing an existing period label means "correct that one" — show
  // its current number so nobody overwrites a reading without realizing.
  const clash = editing
    ? undefined
    : values.find((v) => v.period_label.toLowerCase() === period.trim().toLowerCase())

  React.useEffect(() => {
    if (clash) {
      setValue(String(clash.value))
      setNote(clash.note ?? '')
      setPeriodStart(clash.period_start)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clash?.id])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    const numeric = Number(value)
    if (!period.trim()) return setError('Give the period a label, e.g. \u201cSprint 12\u201d.')
    if (!Number.isFinite(numeric)) return setError('Enter a number.')

    setBusy(true)
    setError(null)
    try {
      const row = {
        period_label: period.trim(),
        period_start: periodStart,
        value: numeric,
        note: note.trim() || null,
      }
      // team_id and recorded_by are filled in by a trigger from the metric, so a
      // client can't file a reading under another team. On update the trigger
      // leaves the original recorder in place.
      const { error: err } = editing
        ? await sb().from('metric_values').update(row).eq('id', editing.id)
        : await sb()
            .from('metric_values')
            .upsert({ ...row, metric_id: metric.id, team_id: metric.team_id }, {
              onConflict: 'metric_id,period_label',
            })
      if (err) throw err
      onSaved()
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err)
      setError(
        /duplicate key|unique/i.test(raw)
          ? `This metric already has a reading for \u201c${period.trim()}\u201d.`
          : raw,
      )
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!editing) return
    if (
      !window.confirm(
        `Delete the ${editing.period_label} reading of ` +
          `${formatMetric(editing.value, metric.unit)}? This can't be undone.`,
      )
    ) {
      return
    }
    setBusy(true)
    const { error: err } = await sb().from('metric_values').delete().eq('id', editing.id)
    setBusy(false)
    if (err) return setError(err.message)
    onSaved()
  }

  return (
    <form onSubmit={save}>
      <DialogHeader>
        <DialogTitle>
          {editing ? 'Edit' : clash ? 'Update' : 'Record'} {metric.label}
        </DialogTitle>
        <DialogDescription>
          {editing
            ? `Correcting the ${editing.period_label} reading. Renaming the period is fine.`
            : clash
              ? `${clash.period_label} already has a reading of ${formatMetric(clash.value, metric.unit)}. Saving replaces it.`
              : 'One number per period. Re-recording the same period updates it.'}
        </DialogDescription>
      </DialogHeader>

      <div className="my-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="period">Period</Label>
            <Input id="period" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="value">
              Value <span className="font-normal text-muted-foreground">({metric.unit})</span>
            </Label>
            <Input
              id="value"
              inputMode="decimal"
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="start">Period start</Label>
          <Input
            id="start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Used to order the trend line.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="note">Note (optional)</Label>
          <Textarea
            id="note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything that explains this number"
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <DialogFooter className="sm:justify-between">
        {editing ? (
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => void remove()}
            disabled={busy}
          >
            <Trash2 /> Delete
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {editing ? 'Save changes' : clash ? 'Update reading' : 'Save reading'}
          </Button>
        </div>
      </DialogFooter>
    </form>
  )
}

function ActivityProgress({
  activities,
  completions,
  userId,
}: {
  activities: Activity[]
  completions: ActivityCompletion[]
  userId: string | undefined
}) {
  const total = activities.filter((a) => a.status !== 'skipped').length
  const mine = new Set(completions.filter((c) => c.user_id === userId).map((c) => c.activity_id))
  const done = activities.filter((a) => mine.has(a.id)).length
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)
  const next = activities.find((a) => a.status !== 'complete' && a.status !== 'skipped' && !mine.has(a.id))

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Presentation className="h-4 w-4 text-muted-foreground" /> Your topics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {total === 0 ? (
          <p className="text-sm text-muted-foreground">No topics scheduled yet.</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-semibold tabular-nums">
                {done}
                <span className="text-base font-normal text-muted-foreground"> / {total}</span>
              </span>
              <span className="text-xs text-muted-foreground">{pct}% complete</span>
            </div>
            <Progress value={pct} />
            {next && (
              <div className="pt-1">
                <p className="text-xs text-muted-foreground">Up next</p>
                <Link
                  to={`/activities/${next.id}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {next.title}
                </Link>
              </div>
            )}
          </>
        )}
        <Button variant="ghost" size="sm" className="px-0" asChild>
          <Link to="/activities">
            All topics <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function UpcomingWorkshops({ workshops }: { workshops: Workshop[] }) {
  const now = Date.now()
  const upcoming = workshops
    .filter((w) => w.status === 'scheduled' && w.starts_at && new Date(w.starts_at).getTime() >= now)
    .slice(0, 3)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4 text-muted-foreground" /> Upcoming workshops
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing on the calendar.</p>
        ) : (
          <ul className="space-y-2.5">
            {upcoming.map((w) => (
              <li key={w.id} className="text-sm">
                <div className="font-medium leading-tight">{w.title}</div>
                <div className="text-xs text-muted-foreground">{formatDate(w.starts_at)}</div>
              </li>
            ))}
          </ul>
        )}
        <Button variant="ghost" size="sm" className="px-0" asChild>
          <Link to="/workshops">
            All workshops <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function LatestEvaluation({ evaluations }: { evaluations: Evaluation[] }) {
  const latest = evaluations[0]
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ClipboardCheck className="h-4 w-4 text-muted-foreground" /> Latest evaluation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!latest ? (
          <p className="text-sm text-muted-foreground">None run yet.</p>
        ) : (
          <div className="space-y-1">
            <Link
              to={`/evaluations/${latest.id}`}
              className="text-sm font-medium leading-tight text-primary hover:underline"
            >
              {latest.name}
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{formatDate(latest.conducted_on)}</span>
              <Badge variant={latest.status === 'complete' ? 'success' : 'muted'} className="text-[10px]">
                {latest.status === 'in_progress' ? 'in progress' : latest.status}
              </Badge>
            </div>
            {evaluations.length > 1 && (
              <p className="pt-1 text-xs text-muted-foreground">
                {evaluations.length} evaluations on record
              </p>
            )}
          </div>
        )}
        <Button variant="ghost" size="sm" className="px-0" asChild>
          <Link to="/evaluations">
            All evaluations <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
